import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { registerHomeTiles, type HomeTile } from '../stores/homescreen';
import { createApiClient } from '@room/frontend-sdk';

const apiClient = createApiClient({
  baseURL: '/api',
  onUnauthorized: () => {
    // No-op: plugin registry is non-critical, skip redirect when not authenticated.
    // Home screen's useBackgroundAuth handles re-authentication silently.
  },
});

type PluginRegistryEntry = {
  pluginId: string;
  state: 'enabled' | 'disabled';
};

type PipelineItem = {
  id: string;
  name?: string;
  description?: string;
  status: string;
  kind: string;
  enabled?: boolean;
  manifest?: {
    type?: string;
    ui?: {
      sidebar?: {
        icon?: string;
        label?: string;
      };
    };
  };
};

type PipelineSnapshot = {
  items: PipelineItem[];
  counts: {
    total: number;
    enabled: number;
    disabled: number;
    installed: number;
    review: number;
  };
};

/**
 * Fetches user's plugin registry (enabled/disabled state)
 */
const fetchUserRegistry = async (): Promise<PluginRegistryEntry[]> => {
  try {
    const response = await apiClient.get<{ data?: { plugins: PluginRegistryEntry[] } }>('/plugins/registry');
    return response?.data?.plugins ?? [];
  } catch (error) {
    console.warn('[usePluginRegistry] Failed to fetch user registry:', error);
    return [];
  }
};

/**
 * Fetches pipeline snapshot (all installed plugins)
 */
const fetchPipelineSnapshot = async (): Promise<PipelineSnapshot | null> => {
  try {
    const response = await apiClient.get<{ data?: PipelineSnapshot }>('/developer/pipeline');
    return response?.data ?? null;
  } catch (error) {
    console.warn('[usePluginRegistry] Failed to fetch pipeline snapshot:', error);
    return null;
  }
};

/**
 * Hook to fetch and auto-register enabled plugins as home tiles
 */
export function usePluginRegistry() {
  const {
    data: userRegistry = [],
    isLoading: isLoadingRegistry,
  } = useQuery({
    queryKey: ['plugin-registry'],
    queryFn: fetchUserRegistry,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });

  const {
    data: pipelineSnapshot,
    isLoading: isLoadingPipeline,
  } = useQuery({
    queryKey: ['pipeline-snapshot'],
    queryFn: fetchPipelineSnapshot,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Mirror the latest userRegistry into a ref so per-plugin `visible`
  // closures stay reference-stable across react-query refetches. Without
  // this, the 60s refetch interval would hand registerHomeTile a fresh
  // closure each cycle, defeating the store's idempotent equality check
  // and causing the home grid to re-render every minute.
  const registryRef = useRef(userRegistry);
  registryRef.current = userRegistry;

  // Cache per-plugin `visible` functions by plugin id so they remain
  // referentially stable for the lifetime of the hook instance.
  const visibleFnCache = useRef(new Map<string, () => boolean>());

  // Auto-register enabled plugins as home tiles
  useEffect(() => {
    if (!pipelineSnapshot || !userRegistry) return;

    const enabledPluginIds = new Set(
      userRegistry
        .filter((entry) => entry.state === 'enabled')
        .map((entry) => entry.pluginId)
    );

    const tiles: HomeTile[] = pipelineSnapshot.items
      .filter((plugin) => enabledPluginIds.has(plugin.id))
      .map((plugin) => {
        const icon = plugin.manifest?.ui?.sidebar?.icon ?? '🧩';
        const label = plugin.manifest?.ui?.sidebar?.label ?? plugin.name ?? plugin.id;

        let visible = visibleFnCache.current.get(plugin.id);
        if (!visible) {
          visible = () => {
            const entry = registryRef.current.find((e) => e.pluginId === plugin.id);
            return entry?.state === 'enabled';
          };
          visibleFnCache.current.set(plugin.id, visible);
        }

        return {
          id: `plugin-${plugin.id}`,
          label,
          icon,
          route: `/plugins/${plugin.id}`,
          section: 'main',
          order: 100, // Plugins appear after core apps
          visible,
        };
      });

    if (tiles.length > 0) {
      registerHomeTiles(tiles);
    }
  }, [pipelineSnapshot, userRegistry]);

  return {
    userRegistry,
    pipelineSnapshot,
    isLoading: isLoadingRegistry || isLoadingPipeline,
    enabledPlugins: userRegistry.filter((entry) => entry.state === 'enabled'),
  };
}
